import { useMutation } from "@apollo/client";
import { noop } from "lodash";
import { ReactNode, useCallback, useContext, useEffect, useState } from "react";
import {
  ComplianceProgramCalibrationStatus,
  ComplianceProgramCalibrationType,
  ComplianceProgramPerformanceReportRowFragment,
  UpdateCalibrationStatusMutation,
  UpdateCalibrationStatusMutationVariables,
} from "types/graphql-schema";

import PerformanceAssessmentReportContext from "@apps/reporting/context/performance-assessment-report-context";
import updateCalibrationStatusMutation from "@apps/reporting/graphql/update-calibration-status-mutation";
import useLabel from "@apps/use-label/use-label";
import { successNotificationVar } from "@cache/cache";

import {
  getCalibrateConfirmation,
  getCalibrateSuccess,
} from "./confirmation-strings";

/**
 * "Mark as Calibrated" for one row: whether the viewer may do it now, the
 * click with its confirm dialog, and whether the save is running. The row
 * calls it once and gives the result to its menu and to the Calibration tab's
 * "Mark as Calibrated" button, so both follow the same rule.
 */
const useMarkCalibrated = ({
  reportRow,
  onRowActionConfirm,
}: {
  reportRow: ComplianceProgramPerformanceReportRowFragment;
  onRowActionConfirm: (message: ReactNode) => Promise<boolean>;
}) => {
  const label = useLabel();
  const { complianceProgram, complianceProgramId } = useContext(
    PerformanceAssessmentReportContext,
  );
  const target = reportRow.user;

  const [updateCalibrationStatus, { loading: isMarkingCalibrated }] =
    useMutation<
      UpdateCalibrationStatusMutation,
      UpdateCalibrationStatusMutationVariables
    >(updateCalibrationStatusMutation);
  // The save can end one render before the row shows Calibrated. Stay
  // disabled until the status changes, so a second click sends nothing.
  const [isAwaitingRowUpdate, setIsAwaitingRowUpdate] = useState(false);
  useEffect(() => {
    setIsAwaitingRowUpdate(false);
  }, [reportRow.calibrationStatus]);

  // The server gives the permission only when the review uses calibration, and
  // in post-calibration only after the manager review is submitted.
  const canMarkCalibrated =
    (complianceProgram?.calibration ===
      ComplianceProgramCalibrationType.PreCalibration ||
      complianceProgram?.calibration ===
        ComplianceProgramCalibrationType.PostCalibration) &&
    reportRow.canCalibrateAnswer.permission &&
    reportRow.calibrationStatus ===
      ComplianceProgramCalibrationStatus.NotStarted;

  const markCalibrated = useCallback(async () => {
    const confirmMsg = getCalibrateConfirmation({
      label,
      calibrationType: complianceProgram?.calibration,
    });
    const confirmation = await onRowActionConfirm(confirmMsg);
    if (confirmation) {
      updateCalibrationStatus({
        variables: {
          complianceProgramId,
          targetId: target.id,
          status: ComplianceProgramCalibrationStatus.Calibrated,
        },
        // The Calibration tab's list counts the calibrated people per group.
        refetchQueries: ["getComplianceProgramCalibrationGroups"],
        onCompleted: () => {
          setIsAwaitingRowUpdate(true);
          const successMsg = getCalibrateSuccess({
            label,
            calibrationType: complianceProgram?.calibration,
            targetName: target.name,
          });
          successNotificationVar({
            title: successMsg,
          });
        },
        // The error link already shows the server's message.
        onError: noop,
      });
    }
  }, [
    complianceProgram,
    complianceProgramId,
    label,
    onRowActionConfirm,
    target,
    updateCalibrationStatus,
  ]);

  return {
    canMarkCalibrated,
    markCalibrated,
    isMarkingCalibrated: isMarkingCalibrated || isAwaitingRowUpdate,
  };
};

export default useMarkCalibrated;
